<template>
  <section class="relative">
    <div class="mb-3 flex items-center justify-between gap-3">
      <div class="flex items-center gap-1">
        <button
          type="button"
          :class="toolbarButtonClass(editor?.isActive('bold'))"
          :disabled="!canEdit || !editor || mode !== 'rich'"
          aria-label="Bold"
          title="Bold"
          @click="runCommand(() => editor.chain().focus().toggleBold().run())"
        >
          <span class="text-sm font-semibold">B</span>
        </button>
        <button
          type="button"
          :class="toolbarButtonClass(editor?.isActive('italic'))"
          :disabled="!canEdit || !editor || mode !== 'rich'"
          aria-label="Italic"
          title="Italic"
          @click="runCommand(() => editor.chain().focus().toggleItalic().run())"
        >
          <span class="text-sm italic">I</span>
        </button>
        <button
          type="button"
          :class="toolbarButtonClass(editor?.isActive('heading', { level: 1 }))"
          :disabled="!canEdit || !editor || mode !== 'rich'"
          aria-label="Heading 1"
          title="Heading 1"
          @click="runCommand(() => editor.chain().focus().toggleHeading({ level: 1 }).run())"
        >
          <span class="text-xs font-semibold">H1</span>
        </button>
        <button
          type="button"
          :class="toolbarButtonClass(editor?.isActive('heading', { level: 2 }))"
          :disabled="!canEdit || !editor || mode !== 'rich'"
          aria-label="Heading 2"
          title="Heading 2"
          @click="runCommand(() => editor.chain().focus().toggleHeading({ level: 2 }).run())"
        >
          <span class="text-xs font-semibold">H2</span>
        </button>
        <button
          type="button"
          :class="toolbarButtonClass(editor?.isActive('bulletList'))"
          :disabled="!canEdit || !editor || mode !== 'rich'"
          aria-label="Bullet list"
          title="Bullet list"
          @click="runCommand(() => editor.chain().focus().toggleBulletList().run())"
        >
          <span class="text-base leading-none">•</span>
        </button>
        <button
          type="button"
          :class="toolbarButtonClass(editor?.isActive('codeBlock'))"
          :disabled="!canEdit || !editor || mode !== 'rich'"
          aria-label="Code block"
          title="Code block"
          @click="runCommand(() => editor.chain().focus().toggleCodeBlock().run())"
        >
          <span class="text-xs font-semibold font-mono">&lt;/&gt;</span>
        </button>
        <button
          type="button"
          :class="toolbarButtonClass(editor?.isActive('blockquote'))"
          :disabled="!canEdit || !editor || mode !== 'rich'"
          aria-label="Quote"
          title="Quote"
          @click="runCommand(() => editor.chain().focus().toggleBlockquote().run())"
        >
          <span class="text-base font-serif font-bold leading-none">"</span>
        </button>
      </div>

      <div class="inline-flex bg-slate-100 p-0.5 dark:bg-slate-800">
        <button
          type="button"
          :class="modeButtonClass(mode === 'rich')"
          :disabled="!canEdit"
          @click="setMode('rich')"
        >
          Rich
        </button>
        <button
          type="button"
          :class="modeButtonClass(mode === 'markdown')"
          :disabled="!canEdit"
          @click="setMode('markdown')"
        >
          MD
        </button>
      </div>
    </div>

    <EditorContent
      v-show="mode === 'rich'"
      :editor="editor"
      :class="editorClass"
    />

    <textarea
      v-show="mode === 'markdown'"
      ref="textareaRef"
      :value="localMarkdown"
      :disabled="!canEdit"
      aria-label="Description markdown"
      class="min-h-[24rem] w-full resize-none bg-slate-50 px-1 py-2 font-mono text-sm leading-6 text-slate-900 outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:text-slate-500 dark:bg-slate-900 dark:text-slate-100 dark:caret-slate-100 dark:disabled:text-slate-500"
      @input="handleRawInput"
    />
  </section>
</template>

<script setup>
import { computed, nextTick, onBeforeUnmount, ref, watch } from 'vue'
import StarterKit from '@tiptap/starter-kit'
import { EditorContent, useEditor } from '@tiptap/vue-3'
import { Markdown } from 'tiptap-markdown'
import { MARKDOWN_PROSE_CLASSES } from './MarkdownView.vue'

const props = defineProps({
  modelValue: {
    type: String,
    default: '',
  },
  canEdit: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:modelValue'])

const mode = ref('rich')
const textareaRef = ref(null)
const localMarkdown = ref(props.modelValue || '')
const suppressEditorUpdate = ref(false)

const editor = useEditor({
  content: localMarkdown.value,
  editable: props.canEdit,
  extensions: [
    StarterKit,
    Markdown.configure({
      html: false,
      tightLists: true,
      bulletListMarker: '-',
      transformCopiedText: true,
    }),
  ],
  onUpdate: ({ editor: activeEditor }) => {
    if (suppressEditorUpdate.value) return

    localMarkdown.value = activeEditor.storage.markdown.getMarkdown()
    emit('update:modelValue', localMarkdown.value)
  },
})

const editorClass = computed(() => [
  MARKDOWN_PROSE_CLASSES,
  'min-h-[24rem] px-1 py-2 outline-none [&_.ProseMirror]:min-h-[24rem] [&_.ProseMirror]:caret-slate-900 [&_.ProseMirror]:outline-none [&_.ProseMirror]:focus:outline-none dark:[&_.ProseMirror]:caret-slate-100',
  props.canEdit ? '' : 'cursor-not-allowed text-slate-500 dark:text-slate-500',
])

watch(
  () => props.modelValue,
  (nextValue) => {
    const nextMarkdown = nextValue || ''
    if (nextMarkdown === localMarkdown.value) return

    localMarkdown.value = nextMarkdown
    if (mode.value === 'rich') {
      setEditorMarkdown(nextMarkdown)
    }
    nextTick(autogrow)
  }
)

watch(
  () => props.canEdit,
  (editable) => {
    editor.value?.setEditable(editable)
  }
)

watch(mode, async (nextMode) => {
  if (nextMode === 'rich') {
    setEditorMarkdown(localMarkdown.value)
  }

  await nextTick()
  autogrow()
})

onBeforeUnmount(() => {
  editor.value?.destroy()
})

function setMode(nextMode) {
  if (!props.canEdit) return
  mode.value = nextMode
}

function runCommand(callback) {
  if (!props.canEdit || !editor.value || mode.value !== 'rich') return
  callback()
}

function handleRawInput(event) {
  if (!props.canEdit) return

  localMarkdown.value = event.target.value
  emit('update:modelValue', localMarkdown.value)
  resizeTextarea(event.target)
}

function setEditorMarkdown(markdown) {
  if (!editor.value) return

  suppressEditorUpdate.value = true
  editor.value.commands.setContent(markdown || '', { emitUpdate: false })
  nextTick(() => {
    suppressEditorUpdate.value = false
  })
}

function autogrow() {
  if (mode.value !== 'markdown') return
  resizeTextarea(textareaRef.value)
}

function resizeTextarea(element) {
  if (!element) return

  element.style.height = 'auto'
  element.style.height = `${Math.max(element.scrollHeight, 384)}px`
}

function toolbarButtonClass(active) {
  return [
    'flex h-8 w-8 items-center justify-center text-slate-600 outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:text-slate-400 dark:text-slate-400 dark:disabled:text-slate-600',
    active ? 'bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300' : 'hover:bg-slate-100 hover:text-slate-950 dark:hover:bg-slate-800 dark:hover:text-slate-100',
  ]
}

function modeButtonClass(active) {
  return [
    'px-3 py-1.5 text-sm font-semibold outline-none focus:ring-2 focus:ring-indigo-500 disabled:cursor-not-allowed disabled:text-slate-400 dark:disabled:text-slate-600',
    active ? 'bg-indigo-600 text-slate-50' : 'text-slate-600 hover:bg-slate-200 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-700 dark:hover:text-slate-100',
  ]
}
</script>
